import { HStack, Text, VStack } from '@chakra-ui/react';
import MenuList from '@components/common/menu/MenuList';
import { Slider } from '@components/ui/slider';
import { useCameraCommand } from '@hooks/useCameraCommand';
import { Suspense, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { CameraType } from '@/types/sensor/configuration.types';
import { ActiveMonitoringSensorByType } from '@/types/sensor/monitoring.types';
import { CameraIrAlgoEnum, VideoStatus } from '@/types/sensor/sensors';

interface MWIRFiltersMenuProps {
  monitoringSensor: ActiveMonitoringSensorByType<CameraType>;
  subCameraStatus: VideoStatus;
}

export default function MWIRFiltersMenu({ monitoringSensor, subCameraStatus }: Readonly<MWIRFiltersMenuProps>) {
  const useCommand = useCameraCommand({
    monitoringSensor,
  });

  const algo = useMemo(() => [subCameraStatus.algo], [subCameraStatus]);

  const gain = useMemo(() => [subCameraStatus.gain], [subCameraStatus]);
  const gamma = useMemo(() => [subCameraStatus.gamma], [subCameraStatus]);
  const offset = useMemo(() => [subCameraStatus.offset], [subCameraStatus]);

  return (
    <Suspense fallback={null}>
      <MenuList
        buttonWidth="288px"
        parentLabel="cameras.cameraMenu.mwirFilters"
        menuItems={Object.entries(CameraIrAlgoEnum).reduce(
          (accumulator, currentValue) => ({
            ...accumulator,
            [currentValue[1]]: {
              label: `cameras.cameraMenu.${currentValue[0]}`,
              isLoading: false,
              onAction: () =>
                currentValue[1] === CameraIrAlgoEnum.UNKNOWN
                  ? useCommand.resetIrConfig(subCameraStatus.id, monitoringSensor.configuration)
                  : useCommand.toggleIrConfig(currentValue[1], subCameraStatus.id, monitoringSensor.configuration),
            },
          }),
          {},
        )}
        selectedOptions={Object.entries(algo)[1]}
      />
      <VStack
        width="292px"
        border="2px solid"
        userSelect="none"
        paddingLeft={2}
        paddingRight={1}
        paddingY={1.5}
        gap={1.5}
        textStyle="screenStd"
        borderTop={0}
        borderColor="neutral.black"
        alignItems="start"
        height="min-content"
        backgroundColor="neutral.900-op80"
      >
        <Text>
          <FormattedMessage id="cameras.cameraMenu.gain" />
        </Text>
        <HStack width="100%" gap={2}>
          <Slider
            width="100%"
            min={0}
            max={255}
            step={1}
            value={gain}
            onValueChange={(value) => useCommand.setIrConfig(value.value[0], null, null, subCameraStatus.id)}
          />
          <Text width="32px" textStyle="screenSm" color="neutral.300">
            {subCameraStatus.gain}
          </Text>
        </HStack>
        <Text>
          <FormattedMessage id="cameras.cameraMenu.gamma" />
        </Text>
        <HStack width="100%" gap={2}>
          <Slider
            width="100%"
            min={0}
            max={255}
            step={1}
            value={gamma}
            onValueChange={(value) => useCommand.setIrConfig(null, null, value.value[0], subCameraStatus.id)}
          />
          <Text width="32px" textStyle="screenSm" color="neutral.300">
            {subCameraStatus.gamma}
          </Text>
        </HStack>
        <Text>
          <FormattedMessage id="cameras.cameraMenu.offset" />
        </Text>
        <HStack width="100%" gap={2}>
          <Slider
            width="100%"
            min={0}
            max={255}
            step={1}
            value={offset}
            onValueChange={(value) => useCommand.setIrConfig(null, value.value[0], null, subCameraStatus.id)}
          />
          <Text width="32px" textStyle="screenSm" color="neutral.300">
            {subCameraStatus.offset}
          </Text>
        </HStack>
      </VStack>
    </Suspense>
  );
}
